import { apiCall, isBackendConfigured } from './api-call'
import {
  QuickPassPublicSharePagePayload,
  QuickPassSharePage,
  QuickPassSharePageVisibility,
  ShareSlugAvailabilityResult,
} from '../_types/quickpass-share-page'

interface CheckSlugInput {
  slug: string
  quickPassUserId: string
}

interface SavePageInput {
  quickPassUserId: string
  slug: string
  headline?: string
  customMessage?: string
  visibility?: Partial<QuickPassSharePageVisibility>
  isActive?: boolean
}

interface SavePageResult {
  page: QuickPassSharePage
  status: 'created' | 'updated'
}

const checkShareSlugAvailability = async (
  input: CheckSlugInput,
): Promise<ShareSlugAvailabilityResult | null> => {
  if (isBackendConfigured() === false) return null

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'checksharepageslug',
      data: { slug: input.slug, quickPassUserId: input.quickPassUserId },
    })
    if (response.code !== 200) throw new Error(response.message)
    return JSON.parse(response.data) as ShareSlugAvailabilityResult
  } catch (error) {
    console.warn('[share-page] checkSlug failed —', error)
    return null
  }
}

const getQuickPassSharePage = async (
  quickPassUserId: string,
): Promise<QuickPassSharePage | null> => {
  if (isBackendConfigured() === false) return null

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'getsharepage',
      data: { quickPassUserId },
    })
    if (response.code === 404) return null
    if (response.code !== 200) throw new Error(response.message)
    return JSON.parse(response.data) as QuickPassSharePage
  } catch (error) {
    console.warn('[share-page] getSharePage failed —', error)
    return null
  }
}

const saveQuickPassSharePage = async (
  input: SavePageInput,
): Promise<SavePageResult | null> => {
  if (isBackendConfigured() === false) return null

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'createorupdatesharepage',
      data: {
        quickPassUserId: input.quickPassUserId,
        slug: input.slug,
        headline: input.headline,
        customMessage: input.customMessage,
        visibility: input.visibility,
        isActive: input.isActive,
      },
    })
    if (response.code !== 200) throw new Error(response.message)
    return JSON.parse(response.data) as SavePageResult
  } catch (error) {
    console.warn('[share-page] save failed —', error)
    return null
  }
}

const regenerateQuickPassShareToken = async (
  quickPassUserId: string,
): Promise<QuickPassSharePage | null> => {
  if (isBackendConfigured() === false) return null

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'regeneratesharetoken',
      data: { quickPassUserId },
    })
    if (response.code !== 200) throw new Error(response.message)
    return JSON.parse(response.data) as QuickPassSharePage
  } catch (error) {
    console.warn('[share-page] regenerate failed —', error)
    return null
  }
}

type PublicSharePageResult =
  | { kind: 'ok'; payload: QuickPassPublicSharePagePayload }
  | { kind: 'not-found' }
  | { kind: 'invalid-link' }
  | { kind: 'disabled' }
  | { kind: 'error'; message: string }

const getPublicQuickPassSharePage = async (
  slug: string,
  token: string,
): Promise<PublicSharePageResult> => {
  if (isBackendConfigured() === false) {
    return { kind: 'error', message: 'Backend not configured locally.' }
  }

  try {
    const response = await apiCall({
      group: 'quickpasspublic',
      command: 'getsharepage',
      data: { slug, token },
    })

    if (response.code === 200) {
      return {
        kind: 'ok',
        payload: JSON.parse(response.data) as QuickPassPublicSharePagePayload,
      }
    }
    if (response.code === 404) return { kind: 'not-found' }
    if (response.code === 401) return { kind: 'invalid-link' }
    if (response.code === 410) return { kind: 'disabled' }
    return { kind: 'error', message: response.message }
  } catch (error) {
    return {
      kind: 'error',
      message: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

export {
  checkShareSlugAvailability,
  getQuickPassSharePage,
  saveQuickPassSharePage,
  regenerateQuickPassShareToken,
  getPublicQuickPassSharePage,
}
export type { PublicSharePageResult }
